import { useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAttachMetaTracking, useCreateMetaShipment, useMetaCredentials, useResetMetaShipping } from "@/hooks/use-meta-orders";
import { groupByStore } from "@/lib/shipping/group-credentials";
import { formatCurrency } from "@/lib/utils";
import type { MetaOrder } from "@/services/meta-orders";

type DialogProps = { meta: MetaOrder; open: boolean; onOpenChange: (open: boolean) => void };

const metaTitle = (meta: MetaOrder) => `${meta.number} · ${meta.storeNames.join(" + ")}`;

/** Nalog kod kurira za cijelu meta narudžbu: račun se bira kroz sve shopove (pošiljalac je shop računa). */
export function MetaShipmentDialog({ meta, open, onOpenChange }: DialogProps) {
  const { data: credentials, isLoading } = useMetaCredentials(open);
  const create = useCreateMetaShipment();
  const [credentialId, setCredentialId] = useState("");
  const [numberOfPackages, setNumberOfPackages] = useState(1);
  const [allowOpening, setAllowOpening] = useState(false);

  function close() {
    if (create.isPending) return;
    setCredentialId("");
    setNumberOfPackages(1);
    setAllowOpening(false);
    onOpenChange(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      const r = await create.mutateAsync({ id: meta.id, credentialId, numberOfPackages, allowOpening });
      if (r.success) {
        toast.success(`${meta.number}: nalog kreiran${r.trackingNumber ? ` — ${r.trackingNumber}` : ""}`);
      } else if (r.code === "timeout") {
        toast.warning(r.error || "Timeout pri slanju — provjerite portal kurira");
      } else {
        // Forma ostaje popunjena da se može pokušati ponovo (npr. drugi račun).
        toast.error(r.error || "Nalog nije kreiran");
        return;
      }
      close();
    } catch (err: any) {
      toast.error(err?.message || "Greška pri kreiranju naloga");
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-md" onInteractOutside={(e) => { if (create.isPending) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle>Kreiraj nalog</DialogTitle>
          <DialogDescription>
            {metaTitle(meta)} · otkupnina {formatCurrency(meta.codAmount, "KM")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          {isLoading ? (
            <p className="text-xs text-muted-foreground">Učitavanje...</p>
          ) : !credentials?.length ? (
            <div className="bg-yellow-500/10 border border-yellow-500/20 p-3 text-xs text-yellow-700">
              Nema aktivnih kurirskih naloga. Dodajte nalog u Postavkama.
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`meta-credential-${meta.id}`}>Kurirska služba</Label>
                <Select value={credentialId} onValueChange={setCredentialId}>
                  <SelectTrigger id={`meta-credential-${meta.id}`} className="w-full"><SelectValue placeholder="Odaberi" /></SelectTrigger>
                  <SelectContent>
                    {groupByStore(credentials).map(([storeName, group]) => (
                      <SelectGroup key={storeName || "—"}>
                        <SelectLabel>{storeName || "Bez trgovine"}</SelectLabel>
                        {group.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            <div className="flex items-center gap-2">
                              <span className="w-2.5 h-2.5 shrink-0" style={{ backgroundColor: c.color ?? undefined }} />
                              {c.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">Pošiljalac je shop kojem račun pripada.</p>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`meta-packages-${meta.id}`}>Broj paketa</Label>
                <Input id={`meta-packages-${meta.id}`} type="number" min={1} value={numberOfPackages} onChange={(e) => setNumberOfPackages(Number(e.target.value))} />
              </div>

              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <Checkbox checked={allowOpening} onCheckedChange={(v) => setAllowOpening(!!v)} />
                Dozvoli otvaranje pošiljke
              </label>
            </>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={close} disabled={create.isPending}>Otkaži</Button>
            <Button
              type="submit"
              disabled={!credentialId || !Number.isInteger(numberOfPackages) || numberOfPackages < 1 || create.isPending}
            >
              {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Kreiraj nalog
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Ručni unos trackinga kad je kurir pukao na timeout, a pošiljka je ipak kreirana u njegovom portalu. */
export function MetaAttachTrackingDialog({ meta, open, onOpenChange }: DialogProps) {
  const attach = useAttachMetaTracking();
  const [tracking, setTracking] = useState("");

  function close() {
    if (attach.isPending) return;
    setTracking("");
    onOpenChange(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      const r = await attach.mutateAsync({ id: meta.id, trackingNumber: tracking.trim() });
      toast.success(`${meta.number}: tracking ${r.trackingNumber} unesen`);
      close();
    } catch (err: any) {
      toast.error(err?.message || "Greška pri unosu trackinga");
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Unesi tracking</DialogTitle>
          <DialogDescription>{metaTitle(meta)}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <p className="text-xs text-muted-foreground">
            Pronađite pošiljku u portalu kurira i upišite njen tracking broj. Vezat će se za sve narudžbe u spojenoj pošiljci.
          </p>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`meta-tracking-${meta.id}`}>Tracking broj</Label>
            <Input
              id={`meta-tracking-${meta.id}`}
              value={tracking}
              onChange={(e) => setTracking(e.target.value)}
              className="font-mono"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close} disabled={attach.isPending}>Otkaži</Button>
            <Button type="submit" disabled={!tracking.trim() || attach.isPending}>
              {attach.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Sačuvaj
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Reset naloga meta narudžbe: briše samo lokalne zapise, paket kod kurira se briše ručno. */
export function MetaResetShippingDialog({ meta, open, onOpenChange }: DialogProps) {
  const reset = useResetMetaShipping();

  async function handleReset() {
    try {
      await reset.mutateAsync(meta.id);
      toast.success(`${meta.number}: nalog resetovan`);
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err?.message || "Greška pri resetovanju naloga");
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!reset.isPending) onOpenChange(v); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reset naloga</DialogTitle>
          <DialogDescription>
            {metaTitle(meta)}
            {meta.shipment?.trackingNumber ? ` · ${meta.shipment.trackingNumber}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-3 items-start bg-yellow-500/10 border border-yellow-500/20 p-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600 shrink-0 mt-0.5" />
          <div className="text-xs text-yellow-700">
            <p className="font-medium mb-1">Upozorenje</p>
            <p>Ova akcija samo briše podatke o pošiljci iz sistema. Morate ručno otići na panel kurirske službe i obrisati paket prije resetovanja.</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={reset.isPending}>Otkaži</Button>
          <Button variant="destructive" onClick={handleReset} disabled={reset.isPending}>
            {reset.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Resetuj
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
